/**
 * @fileoverview Monthly quota display component.
 * @summary Displays monthly event quota with progress bar and alerts.
 *
 * @package KaraoNow
 * @module apps/front/src/components/MonthlyQuotaDisplay
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { getBillingPeriod, getOrganizerLimits } from '../utils/planLimits.js'
import { getLocaleFromLanguage } from '../utils/i18n/index.js'
import { SubscriptionType } from '@karaonow/shared'
import type { FC } from 'react'
import type { User } from '../store/slices/authSlice.js'
import type { Event } from '../store/slices/eventsSlice.js'

interface MonthlyQuotaDisplayProps {
  /** Current user */
  user: User | null
  /** All events */
  events: Event[]
  /** Callback when upgrade button is clicked */
  onUpgrade?: () => void
  /** Additional className */
  className?: string
}

/**
 * @summary Displays monthly event quota with progress bar.
 * @param {MonthlyQuotaDisplayProps} props - Component props.
 * @returns {ReactElement} Monthly quota display or null.
 */
const MonthlyQuotaDisplay: FC<MonthlyQuotaDisplayProps> = ({
  user,
  events,
  onUpgrade,
  className = '',
}) => {
  const { t, i18n } = useTranslation()
  // console.log("MonthlyQuotaDisplay", user)
  if (!user) return null

  const organizerLimits = getOrganizerLimits(user)
  // console.log('organizerLimits', organizerLimits)
  
  // Only show for PRO and PREMIUM (monthly quota plans)
  // if (!organizerLimits.isMonthlyQuota || organizerLimits.maxEvents === Infinity) {
  //   return null
  // }

  
  const billingPeriod = getBillingPeriod(user)
  
  
  // Count events with date in billing period (exclude ONE_SHOT, include hidden events)
  // Hidden events still count in the monthly quota
  // Events created before subscription are counted if their date is in the billing period
  // Logique conditionnelle : PRO/PREMIUM compte 'subscription', FREE compte 'free'
  const eventsInPeriod = billingPeriod
    ? events.filter((e) => {
        const eventDate = new Date(e.date) // Use event date, not creation date
        const inPeriod = eventDate >= billingPeriod.start && eventDate <= billingPeriod.end

        if (!inPeriod) return false

        // Logique conditionnelle selon le plan
        if (organizerLimits.isMonthlyQuota) {
          // Pour PRO/PREMIUM : compter uniquement 'subscription'
          return e.planType === 'subscription'
        } else {
          // Pour FREE : compter uniquement 'free'
          return e.planType === SubscriptionType.FREE || !e.planType
        }
        // Note: We count ALL events (including hidden) for quota calculation
      }).length
    : events.filter((e) => {
        // Même logique conditionnelle si pas de billing period
        if (organizerLimits.isMonthlyQuota) {
          return e.planType === 'subscription'
        } else {
          return e.planType === SubscriptionType.FREE || !e.planType
        }
      }).length
      
  // console.log("events", events)
  const currentCount = eventsInPeriod
  const limit = organizerLimits.maxEvents
  const percentage = limit > 0 ? Math.min((currentCount / limit) * 100, 100) : 0
  const isLimitReached = currentCount >= limit
  const isWarning = percentage >= 80 && !isLimitReached
  const isNotFree = organizerLimits.userPlan !== SubscriptionType.FREE

  // Format billing period dates
  const formatDate = (date: Date) => {
    const locale = getLocaleFromLanguage(i18n.language)
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date)
  }

  const getProgressColor = () => {
    if (isLimitReached) return 'bg-red-600'
    if (isWarning) return 'bg-yellow-500'
    return 'bg-blue-600'
  }

  const getAlertColor = () => {
    if (isLimitReached) return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
    if (isWarning) return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200'
    return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
  }

  const getIcon = () => {
    if (isLimitReached) return faExclamationTriangle
    return faInfoCircle
  }

  return (
    <div className={`rounded-lg border p-4 ${getAlertColor()} ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <FontAwesomeIcon icon={getIcon()} className="w-5 h-5 shrink-0" />
        <h3 className="text-sm font-semibold">
          {isNotFree ? t('subscription.monthlyQuota') || 'Quota mensuel' : t('subscription.freeQuota') || 'Quota gratuit'}
        </h3>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium">
            {t('subscription.eventsUsed', { current: currentCount, limit }) || `${currentCount} / ${limit} événements`}
          </span>
          <span className="text-xs opacity-75">
            {Math.round(percentage)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-2.5 rounded-full transition-all duration-300 ${getProgressColor()}`}
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={currentCount}
            aria-valuemin={0}
            aria-valuemax={limit}
          />
        </div>
      </div>

      {/* Billing Period Info */}
      {billingPeriod && (
        <p className="text-xs opacity-75 mb-3">
          {t('subscription.billingPeriod', {
            start: formatDate(billingPeriod.start),
            end: formatDate(billingPeriod.end),
          }) || `Période : ${formatDate(billingPeriod.start)} - ${formatDate(billingPeriod.end)}`}
        </p>
      )}

      {/* Messages */}
      {isLimitReached && (
        <div className="mb-3">
          <p className="text-sm font-medium mb-2">
            {t('subscription.quotaReached') || 'Quota mensuel atteint'}
          </p>
          <p className="text-xs opacity-90 mb-3">
            {t('subscription.quotaReachedMessage') || 'Vous avez atteint votre limite d\'événements pour cette période. Le quota sera réinitialisé à la fin de votre période de facturation.'}
          </p>
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
            >
              {t('subscription.upgradePlan') || 'Passer au plan supérieur'}
            </button>
          )}
        </div>
      )}

      {isWarning && !isLimitReached && (
        <div className="mb-3">
          <p className="text-sm font-medium mb-2">
            {t('subscription.quotaWarning') || 'Quota mensuel presque atteint'}
          </p>
          <p className="text-xs opacity-90 mb-3">
            {t('subscription.quotaWarningMessage', { remaining: limit - currentCount }) || `Il vous reste ${limit - currentCount} événement(s) pour cette période.`}
          </p>
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
            >
              {t('subscription.upgradePlan') }
            </button>
          )}
        </div>
      )}

      {!isLimitReached && !isWarning && isNotFree && (
        <p className="text-xs opacity-75">
          {t('subscription.quotaInfo', { remaining: limit - currentCount }) || `Il vous reste ${limit - currentCount} événement(s) pour cette période.`}
        </p>
      )}
    </div>
  )
}

export default MonthlyQuotaDisplay

